import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('sehatSetu_token'));
  const [loading, setLoading] = useState(true);

  // Set up axios interceptor
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`);
          setUser(response.data.user);
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const register = useCallback(async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const { token: newToken, user: newUser } = response.data;
    
    localStorage.setItem('sehatSetu_token', newToken);
    setToken(newToken);
    setUser(newUser);
    
    return response.data;
  }, []);

  const login = useCallback(async (credentials) => {
    const response = await axios.post(`${API_URL}/auth/login`, credentials);
    const { token: newToken, user: newUser } = response.data;
    
    localStorage.setItem('sehatSetu_token', newToken);
    setToken(newToken);
    setUser(newUser);
    
    return response.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('sehatSetu_token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const updateLanguage = useCallback(async (language) => {
    if (user) {
      await axios.put(`${API_URL}/auth/language?language=${language}`);
      setUser(prev => ({ ...prev, language }));
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated: !!user,
      register,
      login,
      logout,
      updateLanguage
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
